/*
	Single source of truth for every public icon name authors can pick in
	Prismic, and for every icon rendered anywhere on the public site.

	To add an icon:
	  1. Add a named import below (lucide-react for everything except social
	     brand glyphs, which come from react-icons/fa6 — Lucide is outline-only
	     and ships no brand marks).
	  2. Add one `PublicName: { Component, category }` entry.
	  3. Run `npm run sync-icons` to push the name into every icon Select in
	     the slice model.json and custom-type index.json files, then sync the
	     models to Prismic via Slice Machine.

	NEVER delete or rename a key that has shipped — Prismic documents store the
	string. Retire an icon by pointing its key at a different Component
	(alias), the way Group -> Users and Engineer -> HardHat work below.

	Public names are author-facing: PascalCase, human-friendly, decoupled from
	the lucide export names (Close -> X, Cart -> ShoppingCart). The Prismic
	Select shows these keys verbatim; browse them at /component-library/icons.

	Every icon in this registry ships in the client bundle — slices resolve
	icons at runtime from CMS strings, so nothing here can be tree-shaken
	away. Curate, don't hoard: ~150 is the ceiling, not a target.
*/

import {
	Activity,
	AlarmClock,
	Ambulance,
	Anchor,
	ArrowDown,
	ArrowLeft,
	ArrowRight,
	ArrowUp,
	ArrowUpRight,
	Award,
	Backpack,
	BadgeCheck,
	Bell,
	Binoculars,
	BookOpen,
	Bookmark,
	Box,
	Boxes,
	Briefcase,
	Building,
	Building2,
	Cable,
	Calendar,
	CalendarCheck,
	Camera,
	Check,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	ChevronUp,
	CircleAlert,
	CircleCheck,
	CircleHelp,
	CirclePlay,
	ClipboardCheck,
	ClipboardList,
	Clock,
	Compass,
	Construction,
	Copy,
	Cross,
	Crosshair,
	Download,
	Droplets,
	Earth,
	ExternalLink,
	Eye,
	Factory,
	FileCheck,
	FileDown,
	FileText,
	Film,
	Filter,
	FireExtinguisher,
	Flag,
	Flame,
	Footprints,
	Gauge,
	Globe,
	GraduationCap,
	Handshake,
	HardHat,
	HeartPulse,
	Home,
	Image,
	Info,
	Key,
	Languages,
	LayoutGrid,
	LifeBuoy,
	Link,
	List,
	Lock,
	Mail,
	Map,
	MapPin,
	Maximize,
	Medal,
	Menu,
	Mic,
	Minimize,
	Minus,
	Monitor,
	Mountain,
	MountainSnow,
	Navigation,
	Package,
	Pause,
	Pencil,
	Phone,
	Plane,
	PlaneTakeoff,
	Play,
	Plus,
	Presentation,
	Printer,
	Radar,
	Radio,
	RadioTower,
	Rocket,
	Route,
	Ruler,
	ScrollText,
	Search,
	Settings,
	Share2,
	Shield,
	ShieldAlert,
	ShieldCheck,
	Ship,
	ShoppingCart,
	Siren,
	Smartphone,
	Snowflake,
	Star,
	Stethoscope,
	Tag,
	Target,
	Tent,
	Thermometer,
	Timer,
	TowerControl,
	TrafficCone,
	Trash2,
	TreePine,
	Trees,
	TriangleAlert,
	Trophy,
	Truck,
	Unlock,
	Upload,
	User,
	UserCheck,
	UserPlus,
	Users,
	Video,
	Volume2,
	VolumeX,
	Warehouse,
	Wind,
	Wrench,
	X,
	Zap,
	ZapOff,
} from "lucide-react";

import {
	FaFacebookF,
	FaInstagram,
	FaLinkedinIn,
	FaTiktok,
	FaXTwitter,
	FaYoutube,
} from "react-icons/fa6";

export const ICONS = {
	// ----- Social (brand glyphs; react-icons/fa6) -----
	// Keys match values already saved by FooterItem and the footer custom
	// type's social_links group — do not rename.
	Facebook: { Component: FaFacebookF, category: "Social" },
	Instagram: { Component: FaInstagram, category: "Social" },
	LinkedIn: { Component: FaLinkedinIn, category: "Social" },
	TikTok: { Component: FaTiktok, category: "Social" },
	// "Twitter" is the saved content value; the glyph is the current X mark.
	Twitter: { Component: FaXTwitter, category: "Social" },
	Youtube: { Component: FaYoutube, category: "Social" },

	// ----- Interface & Utilities -----
	ArrowDown: { Component: ArrowDown, category: "Interface & Utilities" },
	ArrowLeft: { Component: ArrowLeft, category: "Interface & Utilities" },
	ArrowRight: { Component: ArrowRight, category: "Interface & Utilities" },
	ArrowUp: { Component: ArrowUp, category: "Interface & Utilities" },
	ArrowUpRight: { Component: ArrowUpRight, category: "Interface & Utilities" },
	Bell: { Component: Bell, category: "Interface & Utilities" },
	Bookmark: { Component: Bookmark, category: "Interface & Utilities" },
	Calendar: { Component: Calendar, category: "Interface & Utilities" },
	CalendarCheck: { Component: CalendarCheck, category: "Interface & Utilities" },
	Check: { Component: Check, category: "Interface & Utilities" },
	CheckCircle: { Component: CircleCheck, category: "Interface & Utilities" },
	ChevronDown: { Component: ChevronDown, category: "Interface & Utilities" },
	ChevronLeft: { Component: ChevronLeft, category: "Interface & Utilities" },
	ChevronRight: { Component: ChevronRight, category: "Interface & Utilities" },
	ChevronUp: { Component: ChevronUp, category: "Interface & Utilities" },
	Clock: { Component: Clock, category: "Interface & Utilities" },
	// Close: authors know "Close"; lucide calls it X. Hit hardcoded by
	// CourseSearch — keep the key.
	Close: { Component: X, category: "Interface & Utilities" },
	Copy: { Component: Copy, category: "Interface & Utilities" },
	Download: { Component: Download, category: "Interface & Utilities" },
	ExternalLink: { Component: ExternalLink, category: "Interface & Utilities" },
	Eye: { Component: Eye, category: "Interface & Utilities" },
	Filter: { Component: Filter, category: "Interface & Utilities" },
	Flag: { Component: Flag, category: "Interface & Utilities" },
	Grid: { Component: LayoutGrid, category: "Interface & Utilities" },
	Home: { Component: Home, category: "Interface & Utilities" },
	Info: { Component: Info, category: "Interface & Utilities" },
	Key: { Component: Key, category: "Interface & Utilities" },
	Link: { Component: Link, category: "Interface & Utilities" },
	List: { Component: List, category: "Interface & Utilities" },
	Lock: { Component: Lock, category: "Interface & Utilities" },
	Mail: { Component: Mail, category: "Interface & Utilities" },
	Menu: { Component: Menu, category: "Interface & Utilities" },
	Minus: { Component: Minus, category: "Interface & Utilities" },
	// Pencil: saved NavigationItem child_icon value — do not rename.
	Pencil: { Component: Pencil, category: "Interface & Utilities" },
	Phone: { Component: Phone, category: "Interface & Utilities" },
	Plus: { Component: Plus, category: "Interface & Utilities" },
	Question: { Component: CircleHelp, category: "Interface & Utilities" },
	Search: { Component: Search, category: "Interface & Utilities" },
	Settings: { Component: Settings, category: "Interface & Utilities" },
	Share: { Component: Share2, category: "Interface & Utilities" },
	Star: { Component: Star, category: "Interface & Utilities" },
	Tag: { Component: Tag, category: "Interface & Utilities" },
	Trash: { Component: Trash2, category: "Interface & Utilities" },
	Unlock: { Component: Unlock, category: "Interface & Utilities" },
	Upload: { Component: Upload, category: "Interface & Utilities" },
	Warning: { Component: TriangleAlert, category: "Interface & Utilities" },
	WarningCircle: { Component: CircleAlert, category: "Interface & Utilities" },

	// ----- Media -----
	Camera: { Component: Camera, category: "Media" },
	Film: { Component: Film, category: "Media" },
	Image: { Component: Image, category: "Media" },
	Maximize: { Component: Maximize, category: "Media" },
	Mic: { Component: Mic, category: "Media" },
	Minimize: { Component: Minimize, category: "Media" },
	Monitor: { Component: Monitor, category: "Media" },
	Pause: { Component: Pause, category: "Media" },
	Play: { Component: Play, category: "Media" },
	PlayCircle: { Component: CirclePlay, category: "Media" },
	Printer: { Component: Printer, category: "Media" },
	Smartphone: { Component: Smartphone, category: "Media" },
	Video: { Component: Video, category: "Media" },
	Volume: { Component: Volume2, category: "Media" },
	VolumeMute: { Component: VolumeX, category: "Media" },

	// ----- Safety & Rescue -----
	Activity: { Component: Activity, category: "Safety & Rescue" },
	AlarmClock: { Component: AlarmClock, category: "Safety & Rescue" },
	Ambulance: { Component: Ambulance, category: "Safety & Rescue" },
	Anchor: { Component: Anchor, category: "Safety & Rescue" },
	Cable: { Component: Cable, category: "Safety & Rescue" },
	Construction: { Component: Construction, category: "Safety & Rescue" },
	Cross: { Component: Cross, category: "Safety & Rescue" },
	Droplets: { Component: Droplets, category: "Safety & Rescue" },
	// Engineer: legacy Icon.jsx case (was a broken react-icons import) —
	// aliased to HardHat so any saved value renders instead of crashing.
	Engineer: { Component: HardHat, category: "Safety & Rescue" },
	FireExtinguisher: { Component: FireExtinguisher, category: "Safety & Rescue" },
	Flame: { Component: Flame, category: "Safety & Rescue" },
	Gauge: { Component: Gauge, category: "Safety & Rescue" },
	HardHat: { Component: HardHat, category: "Safety & Rescue" },
	HeartPulse: { Component: HeartPulse, category: "Safety & Rescue" },
	LifeBuoy: { Component: LifeBuoy, category: "Safety & Rescue" },
	Radio: { Component: Radio, category: "Safety & Rescue" },
	RadioTower: { Component: RadioTower, category: "Safety & Rescue" },
	Ruler: { Component: Ruler, category: "Safety & Rescue" },
	Shield: { Component: Shield, category: "Safety & Rescue" },
	ShieldAlert: { Component: ShieldAlert, category: "Safety & Rescue" },
	ShieldCheck: { Component: ShieldCheck, category: "Safety & Rescue" },
	Siren: { Component: Siren, category: "Safety & Rescue" },
	Snowflake: { Component: Snowflake, category: "Safety & Rescue" },
	Stethoscope: { Component: Stethoscope, category: "Safety & Rescue" },
	Thermometer: { Component: Thermometer, category: "Safety & Rescue" },
	Timer: { Component: Timer, category: "Safety & Rescue" },
	TrafficCone: { Component: TrafficCone, category: "Safety & Rescue" },
	Wind: { Component: Wind, category: "Safety & Rescue" },
	Wrench: { Component: Wrench, category: "Safety & Rescue" },
	Zap: { Component: Zap, category: "Safety & Rescue" },
	ZapOff: { Component: ZapOff, category: "Safety & Rescue" },

	// ----- Location & Structures -----
	Building: { Component: Building, category: "Location & Structures" },
	Building2: { Component: Building2, category: "Location & Structures" },
	Compass: { Component: Compass, category: "Location & Structures" },
	Crosshair: { Component: Crosshair, category: "Location & Structures" },
	Earth: { Component: Earth, category: "Location & Structures" },
	Factory: { Component: Factory, category: "Location & Structures" },
	// Globe: saved NavigationItem child_icon value — do not rename.
	Globe: { Component: Globe, category: "Location & Structures" },
	Map: { Component: Map, category: "Location & Structures" },
	MapPin: { Component: MapPin, category: "Location & Structures" },
	Mountain: { Component: Mountain, category: "Location & Structures" },
	MountainSnow: { Component: MountainSnow, category: "Location & Structures" },
	Navigation: { Component: Navigation, category: "Location & Structures" },
	Route: { Component: Route, category: "Location & Structures" },
	Target: { Component: Target, category: "Location & Structures" },
	Tent: { Component: Tent, category: "Location & Structures" },
	TowerControl: { Component: TowerControl, category: "Location & Structures" },
	TreePine: { Component: TreePine, category: "Location & Structures" },
	Trees: { Component: Trees, category: "Location & Structures" },
	Warehouse: { Component: Warehouse, category: "Location & Structures" },

	// ----- People & Training -----
	Award: { Component: Award, category: "People & Training" },
	BadgeCheck: { Component: BadgeCheck, category: "People & Training" },
	BookOpen: { Component: BookOpen, category: "People & Training" },
	Briefcase: { Component: Briefcase, category: "People & Training" },
	GraduationCap: { Component: GraduationCap, category: "People & Training" },
	// Group: saved NavigationItem child_icon value — aliased to Users.
	Group: { Component: Users, category: "People & Training" },
	Handshake: { Component: Handshake, category: "People & Training" },
	Languages: { Component: Languages, category: "People & Training" },
	Medal: { Component: Medal, category: "People & Training" },
	Presentation: { Component: Presentation, category: "People & Training" },
	Trophy: { Component: Trophy, category: "People & Training" },
	User: { Component: User, category: "People & Training" },
	UserCheck: { Component: UserCheck, category: "People & Training" },
	UserPlus: { Component: UserPlus, category: "People & Training" },
	Users: { Component: Users, category: "People & Training" },

	// ----- Files & Logistics -----
	Backpack: { Component: Backpack, category: "Files & Logistics" },
	Binoculars: { Component: Binoculars, category: "Files & Logistics" },
	Box: { Component: Box, category: "Files & Logistics" },
	Boxes: { Component: Boxes, category: "Files & Logistics" },
	Cart: { Component: ShoppingCart, category: "Files & Logistics" },
	ClipboardCheck: { Component: ClipboardCheck, category: "Files & Logistics" },
	ClipboardList: { Component: ClipboardList, category: "Files & Logistics" },
	FileCheck: { Component: FileCheck, category: "Files & Logistics" },
	FileDown: { Component: FileDown, category: "Files & Logistics" },
	FileText: { Component: FileText, category: "Files & Logistics" },
	Footprints: { Component: Footprints, category: "Files & Logistics" },
	Package: { Component: Package, category: "Files & Logistics" },
	Plane: { Component: Plane, category: "Files & Logistics" },
	PlaneTakeoff: { Component: PlaneTakeoff, category: "Files & Logistics" },
	Radar: { Component: Radar, category: "Files & Logistics" },
	Rocket: { Component: Rocket, category: "Files & Logistics" },
	ScrollText: { Component: ScrollText, category: "Files & Logistics" },
	Ship: { Component: Ship, category: "Files & Logistics" },
	Truck: { Component: Truck, category: "Files & Logistics" },
};
